import { useMemo, useRef, useCallback, useEffect, useState } from 'react'

/**
 * PriceHistoryChartSvg — presentational SVG for price history step chart.
 * Renders axis grid, step path, entry points, today marker, and tooltip anchor.
 *
 * @param {object} props
 * @param {object|null} props.chartData
 * @param {number|null} props.hoveredPointIndex
 * @param {(index: number|null) => void} props.onHoveredPointChange
 * @param {(dateString: string) => string} props.formatDate
 * @param {(value: number|null|undefined) => string} props.formatMoney
 * @param {string} props.totalLabel
 * @param {string} props.sqmLabel
 * @param {string} props.ariaLabel
 * @param {string} props.todayLabel
 */
export function PriceHistoryChartSvg({
  chartData,
  hoveredPointIndex,
  onHoveredPointChange,
  formatDate,
  formatMoney,
  totalLabel,
  sqmLabel,
  ariaLabel,
  todayLabel,
  styles,
}) {
  const chartRef = useRef(null)
  const tooltipRef = useRef(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [tooltipPosition, setTooltipPosition] = useState(null)

  const hoveredPoint = useMemo(() => {
    if (!Number.isInteger(hoveredPointIndex)) return null
    if (!chartData?.points?.[hoveredPointIndex]) return null
    return chartData.points[hoveredPointIndex]
  }, [hoveredPointIndex, chartData])

  useEffect(() => {
    const element = chartRef.current?.parentElement
    if (!element) return undefined

    const updateSize = () => {
      const rect = element.getBoundingClientRect()
      setContainerSize({ width: rect.width, height: rect.height })
    }

    updateSize()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize)
      return () => window.removeEventListener('resize', updateSize)
    }

    const observer = new ResizeObserver(updateSize)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const handleChartMove = useCallback((event) => {
    if (!chartData?.points?.length || !chartRef.current) return

    const rect = chartRef.current.getBoundingClientRect()
    const pointerX = ((event.clientX - rect.left) / rect.width) * chartData.width

    let nearestIndex = 0
    let nearestDistance = Number.POSITIVE_INFINITY

    for (let i = 0; i < chartData.points.length; i += 1) {
      const distance = Math.abs(chartData.points[i].x - pointerX)
      if (distance < nearestDistance) {
        nearestDistance = distance
        nearestIndex = i
      }
    }

    onHoveredPointChange(nearestIndex)
  }, [chartData, onHoveredPointChange])

  const handleChartLeave = useCallback(() => {
    onHoveredPointChange(null)
  }, [onHoveredPointChange])

  useEffect(() => {
    if (!hoveredPoint || !chartData || !tooltipRef.current || !containerSize.width || !containerSize.height) {
      setTooltipPosition(null)
      return undefined
    }

    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const scaleX = containerSize.width / chartData.width
    const scaleY = containerSize.height / chartData.height
    const anchorX = hoveredPoint.x * scaleX
    const anchorY = hoveredPoint.y * scaleY
    const margin = 12
    const isMobileWidth = containerSize.width <= 640
    const edgeThreshold = tooltipRect.width / 2 + margin
    const sideNudge = isMobileWidth ? 10 : 0
    let preferredLeft = anchorX - tooltipRect.width / 2

    if (isMobileWidth && anchorX < edgeThreshold) {
      preferredLeft = anchorX + sideNudge
    } else if (isMobileWidth && anchorX > containerSize.width - edgeThreshold) {
      preferredLeft = anchorX - tooltipRect.width - sideNudge
    }

    const maxLeft = Math.max(margin, containerSize.width - tooltipRect.width - margin)
    const maxTop = Math.max(margin, containerSize.height - tooltipRect.height - margin)

    setTooltipPosition({
      left: Math.min(Math.max(preferredLeft, margin), maxLeft),
      top: Math.min(Math.max(anchorY - tooltipRect.height - margin, margin), maxTop),
    })
  }, [hoveredPoint, chartData, containerSize.width, containerSize.height])

  const renderScale = useMemo(() => {
    if (!chartData || !containerSize.width) return 1
    return Math.max(1, chartData.width / containerSize.width)
  }, [chartData, containerSize.width])
  const textScale = Math.sqrt(renderScale)

  const pointRadius = 4 * renderScale
  const hoveredPointRadius = 5 * renderScale
  const labelFontSize = 18 * textScale
  const axisFontSize = 14 * textScale
  const xAxisDatesTranslateY = 20 * textScale

  if (!chartData) return null

  return (
    <div className={styles.chartCrate}>
      <svg
        ref={chartRef}
        className={styles.chartSvg}
        viewBox={`0 0 ${chartData.width} ${chartData.height}`}
        role="img"
        aria-label={ariaLabel}
        onMouseMove={handleChartMove}
        onMouseLeave={handleChartLeave}
        onClick={handleChartMove}
      >
        {chartData.yTicks.map((tick) => (
          <g key={`y-${tick.value}`}>
            <line
              x1={chartData.padding.left}
              x2={chartData.width - chartData.padding.right}
              y1={tick.y}
              y2={tick.y}
              className={styles.gridLine}
            />
            <text x={0} y={tick.y + 4} className={styles.yLabel} fontSize={axisFontSize}>
              {formatMoney(tick.value)}
            </text>
          </g>
        ))}

        <path d={chartData.stepPath} className={styles.stepPath} />

        {chartData.points.map((point, idx) => (
          <circle
            key={`${point.timestamp}-${point.x}`}
            cx={point.x}
            cy={point.y}
            r={idx === hoveredPointIndex ? hoveredPointRadius : pointRadius}
            className={styles.chartPoint}
            onMouseEnter={() => onHoveredPointChange(idx)}
            onClick={() => onHoveredPointChange(idx)}
          >
            <title>
              {`${formatDate(point.date)} • ${formatMoney(point.price_brutto)} • ${formatMoney(point.price_m2_brutto)}`}
            </title>
          </circle>
        ))}

        {chartData.todayMarker && (
          <g>
            <line
              x1={chartData.todayMarker.x}
              x2={chartData.todayMarker.x}
              y1={chartData.padding.top}
              y2={chartData.height - chartData.padding.bottom}
              className={styles.todayLine}
            />
            <text
              x={chartData.todayMarker.x}
              y={chartData.padding.top - 6}
              textAnchor="middle"
              className={styles.todayLabel}
              fontSize={labelFontSize}
            >
              {todayLabel}
            </text>
          </g>
        )}

        <text
          x={chartData.padding.left}
          y={chartData.height - chartData.padding.bottom + xAxisDatesTranslateY}
          className={styles.xLabel}
          fontSize={labelFontSize}
        >
          {formatDate(chartData.startDate)}
        </text>
        <text
          x={chartData.width - chartData.padding.right}
          y={chartData.height - chartData.padding.bottom + xAxisDatesTranslateY}
          textAnchor="end"
          className={styles.xLabel}
          fontSize={labelFontSize}
        >
          {formatDate(chartData.endDate)}
        </text>
      </svg>

      {hoveredPoint && (
        <div
          ref={tooltipRef}
          className={styles.tooltip}
          style={{
            left: tooltipPosition ? `${tooltipPosition.left}px` : `${(hoveredPoint.x / chartData.width) * 100}%`,
            top: tooltipPosition ? `${tooltipPosition.top}px` : `${(hoveredPoint.y / chartData.height) * 100}%`,
          }}
        >
          <div className={styles.tooltipDate}>{formatDate(hoveredPoint.date)}</div>
          <div>{totalLabel}: {formatMoney(hoveredPoint.price_brutto)}</div>
          <div>{sqmLabel}: {formatMoney(hoveredPoint.price_m2_brutto)}</div>
        </div>
      )}
    </div>
  )
}
